import { useEffect, useRef, useState } from 'react'
import type { CallFacts } from '@communicator/shared'
import { formatClock, roundedBillingLabel } from './format'
import { ops, type CallLeg } from '../../lib/ops'
import { Button, Icon } from '../ui'

/**
 * What a finished call shows where the composer would be.
 *
 * WHY NOT JUST DISABLE THE SEND BUTTON. Voice has no send at all — the adapter
 * throws on the attempt, because 3CX owns the audio and this system never
 * touches it. A greyed-out composer still reads as "you could type here if
 * something were different", and a tech who has just read a transcript will try
 * it. Replacing it says the true thing: the call is over, here is what happened,
 * and the way to say something is to ring back.
 *
 * The composer was already suppressed while a call was RINGING or LIVE. It came
 * back the moment the call ended, which is exactly when someone reading the
 * thread is most likely to type into it.
 *
 * TASK 5 (Tossker repaint), README §4's B2 frame: the per-leg journey is now
 * a horizontal timeline (endpoints side by side, connected by a labelled
 * rule) rather than a vertical list, and a "6:42 → 15m" rounding chip sits
 * beside the log control — see `roundedBillingLabel` in ./format.ts. Both
 * are purely presentational: `handleLog` below still calls `log-call` with
 * exactly `note`/`billable`/`attachRecording`, unchanged from before this
 * task — the rounded-minutes figure is shown, never sent. The closing
 * "Voice is read-only here…" sentence is kept verbatim.
 */
export interface CallSummaryProps {
  call: CallFacts
  /** The handle we know the caller by — a number, or a name once matched. */
  who: string
  /** The thread, so the per-leg journey can be fetched. */
  threadId: string
  /** Whether a ticket is attached — decides where a note actually lands. */
  hasTicket: boolean
  /**
   * Whether the caller is matched to a Halo contact — the gate for Quick Fix.
   * The contact alone is enough: a ticket created with a user_id inherits that
   * contact's client AND site in Halo, which is what makes the closing half of
   * a Quick Fix possible (Halo 400s a close on the default Client/Site;
   * verified live — contact-only creation lands on the contact's real site and
   * closes clean). Computed by ThreadPane as `haloContactId !== null`.
   */
  matched: boolean
  /**
   * The internal notes typed on this thread while the call was up, oldest
   * first, so the box opens as a draft assembled from the dispatcher's own
   * fragments instead of a blank field she retypes from memory.
   *
   * Bodies only, deliberately: no timestamps or author names. This is a
   * starting DRAFT she edits into a coherent note, and anything decorative
   * here is something she has to delete before saving. The fragments remain
   * in the transcript with their attribution intact either way.
   */
  notes?: string[]
  /**
   * The write-up text, owned by the store and keyed by thread.
   *
   * NOT local state, which is what it used to be: this component is not
   * remounted per thread by React on its own, so the text stayed put while
   * the call around it changed and turned up under the next call you clicked.
   * ThreadPane now also keys this component by thread id, which resets
   * everything else here — but a `key` remount would DISCARD the draft, and
   * the whole point is that it is waiting when you come back. So the one
   * piece of state that must outlive the remount lives outside it.
   */
  note: string
  onNoteChange: (value: string) => void
}

const DIRECTION_LABEL: Record<'in' | 'out', string> = {
  in: 'Inbound call',
  out: 'Outbound call',
}

/** "rang 8s · talked 29s" — only the parts that happened. */
function legTiming(leg: CallLeg): string {
  const parts: string[] = []
  if (leg.ringS && leg.ringS > 0) parts.push(`rang ${leg.ringS}s`)
  if (leg.answered) parts.push(`talked ${formatClock(leg.talkS)}`)
  // A leg that neither rang measurably nor was answered is a queue hop, and
  // saying "0s" about it would imply something failed.
  return parts.length > 0 ? parts.join(' · ') : 'passed straight through'
}

export function CallSummary({ call, who, threadId, hasTicket, matched, notes = [], note, onNoteChange }: CallSummaryProps) {
  const [legs, setLegs] = useState<CallLeg[] | null>(null)
  const [billable, setBillable] = useState(true)
  const [saving, setSaving] = useState(false)
  const [logError, setLogError] = useState<string | null>(null)
  const [logged, setLogged] = useState(call.dispoDone)
  /**
   * The draft is seeded EXACTLY ONCE per mount. Three things this guards, all
   * of which are worse than a blank box:
   *
   * - Re-seeding after a save would refill the field with fragments already
   *   written up, and a second press of Log would bill the same call twice.
   *   `logged` covers the call that arrives already dispositioned; this ref
   *   covers the one dispositioned while the summary is open.
   * - The notes arrive asynchronously with the thread detail, so this cannot
   *   be a useState initialiser — at mount there is usually nothing to seed
   *   with yet.
   * - `notes` is rebuilt by the parent's filter on every render, so without
   *   the ref this effect would fire forever.
   */
  const seeded = useRef(false)
  const draft = notes.join('\n')

  useEffect(() => {
    if (seeded.current || logged || draft === '') return
    // Only into an EMPTY box. If she started typing before the fragments
    // loaded — or came back to a draft she left here earlier — her words win.
    // Seeding must never take text out of a person's hands.
    if (note !== '') return
    seeded.current = true
    onNoteChange(draft)
    // `onNoteChange` is recreated per render by the parent; depending on it
    // would re-run this on every render, and the ref would be the only thing
    // stopping a loop. The guards above are what decide, not the identity of
    // the callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, logged, note])

  /**
   * 1B: a call nobody needs a ticket for — finished here, never sent to Halo.
   *
   * `close-thread`, NOT `log-call`. That is not a stylistic choice: `log-call`
   * is gated on a linked ticket (`log-call-needs-ticket`) because a time entry
   * is the billing record, and it also refuses while Halo is unreachable. Both
   * rules are right and neither should be bent for this. `close-thread` on an
   * unlinked thread is the operations layer's own "pure disposal" path — it
   * writes nothing to the record system at all — and the note it carries is
   * kept as a close note, which is searchable, so the vendor who rang in off a
   * tradeshow list can still be found months later.
   */
  async function handleCloseHere() {
    if (!note.trim() || saving) return
    setSaving(true)
    setLogError(null)
    try {
      await ops.call('close-thread', { threadId, note: note.trim() } as never)
      setLogged(true)
      onNoteChange('')
    } catch (e) {
      setLogError(e instanceof Error ? e.message : 'The call could not be closed.')
    } finally {
      setSaving(false)
    }
  }

  /**
   * 1A: Quick Fix — a matched call nobody had a ticket for, dispositioned in
   * one step. Three EXISTING ops in sequence, not a composed operation: each
   * is separately gated, and a mid-chain failure leaves visible, recoverable
   * state (ticket linked + thread open -> the ordinary Log & Close finishes
   * the job). The subject is built here because the thread's `who` is already
   * the resolved contact name on a matched thread.
   */
  async function handleQuickFix() {
    if (!note.trim() || saving) return
    setSaving(true)
    setLogError(null)
    try {
      await ops.call('create-ticket', { threadId, subject: `Quick Fix — ${who}`, quickFix: true } as never)
      await ops.call('log-call', { threadId, note: note.trim(), billable, attachRecording: false } as never)
      await ops.call('close-thread', { threadId, closeTicket: true } as never)
      setLogged(true)
      onNoteChange('')
    } catch (e) {
      setLogError(e instanceof Error ? e.message : 'The Quick Fix could not be completed.')
    } finally {
      setSaving(false)
    }
  }

  async function handleLog() {
    if (!note.trim() || saving) return
    setSaving(true)
    setLogError(null)
    try {
      await ops.call('log-call', {
        threadId,
        note: note.trim(),
        billable,
        // Recording attachment is deliberately not offered: no recording field
        // exists in this PBX's CDR output, and the XAPI's Recordings endpoint
        // refuses at every role we have. A toggle that silently attaches
        // nothing would be worse than its absence.
        attachRecording: false,
      } as never)
      setLogged(true)
      onNoteChange('')
    } catch (e) {
      setLogError(e instanceof Error ? e.message : 'The note could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    ops
      .callLegs(threadId)
      .then((r) => {
        if (!cancelled) setLegs(r.legs)
      })
      .catch(() => {
        // The summary above still stands on the thread's own facts. Showing
        // nothing is better than an error where the journey would be.
      })
    return () => {
      cancelled = true
    }
  }, [threadId])

  // A missed call has no duration worth printing, and "0:00" reads like a
  // recording failure rather than "nobody picked up".
  const answered = call.finish !== 'Missed' && (call.durationS ?? 0) > 0

  const billingChip = answered ? roundedBillingLabel(call.durationS) : null

  return (
    <div
      data-testid="call-summary"
      className="flex-none border-t border-subtle bg-sidebar px-4 py-3"
      role="group"
      aria-label="Call summary"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[13px] font-semibold text-hi">{DIRECTION_LABEL[call.direction]}</span>
        <span className="text-[13px] text-mid">{who}</span>
        {answered ? (
          <span data-testid="call-summary-duration" className="font-mono text-[13px] text-hi">
            {formatClock(call.durationS ?? 0)}
          </span>
        ) : (
          <span data-testid="call-summary-missed" className="text-[13px] text-warn">
            Not answered
          </span>
        )}
        {call.handler ? (
          <span className="text-[12px] text-lo">
            {/* The extension stays even once a name resolves — Andrew asked for
                both, so nobody has to hold "605" and "Mark" in their head as the
                same person. No name (PBX unreachable, no host configured, or the
                extension is genuinely unnamed) renders exactly today's text —
                extensionNames() never blocks this and never invents one. */}
            handled by ext. {call.handler}
            {call.handlerName ? ` - ${call.handlerName}` : ''}
          </span>
        ) : null}
      </div>

      {legs && legs.length > 0 ? (
        // README §4's B2 frame: "endpoints in columns, 2px --slate-300
        // connectors labelled 'talked 3:02' / 'talked 3:40'." One row per
        // leg, each a from-endpoint / connector+timing / to-endpoint triple,
        // laid out left to right and scrolling horizontally if the journey
        // has more legs than the pane is wide.
        <div data-testid="call-journey" className="mt-2 flex items-center gap-1 overflow-x-auto pb-1">
          {legs.map((leg, i) => (
            <div key={i} className="flex flex-none items-center gap-1">
              {i === 0 && <span className="whitespace-nowrap text-[11.5px] font-medium text-hi">{leg.fromLabel || leg.from}</span>}
              <span className="flex flex-none flex-col items-center px-1">
                <span aria-hidden="true" className="h-[2px] w-[30px]" style={{ background: 'var(--border-default)' }} />
                <span className="mt-1 whitespace-nowrap text-[10px] text-lo">{legTiming(leg)}</span>
              </span>
              <span className="whitespace-nowrap text-[11.5px] font-medium text-hi">{leg.toLabel || leg.to}</span>
              {leg.transferred ? <span className="whitespace-nowrap text-[10px] text-info">transferred on</span> : null}
              {leg.endedBy ? (
                <span className="whitespace-nowrap text-[10px] text-lo">
                  {/* The question people actually argue about after a bad call. */}
                  {leg.endedBy === 'caller' ? 'caller hung up' : 'we hung up'}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {billingChip && (
        <span
          data-testid="call-billing-chip"
          className="mt-2 inline-flex items-center whitespace-nowrap rounded-[5px] px-2 py-0.5 font-mono text-[10.5px] font-semibold"
          style={{ background: 'var(--tint-primary)', color: 'var(--brand-primary-text)' }}
        >
          {billingChip}
        </span>
      )}

      {/* Not a composer: there is no send for voice. A note about the call,
          which is the thing a tech actually wants the keyboard for once the
          call is over. log-call also writes a Halo time entry carrying this
          note and the call's duration, so it becomes billable time rather than
          a comment nobody sees. */}
      <div className="mt-2.5">
        {logged ? (
          <p data-testid="call-note-logged" className="text-[12px] text-ok">
            Call logged{call.dispoNote ? `: ${call.dispoNote}` : ''}
          </p>
        ) : null}

        <label htmlFor="call-note" className="sr-only">
          Note about this call
        </label>
        <textarea
          id="call-note"
          data-testid="call-note-input"
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          rows={2}
          placeholder={logged ? 'Add another note about this call…' : 'What happened on this call?'}
          className="w-full resize-none rounded-[8px] border border-subtle bg-card px-2.5 py-1.5 text-[13px] text-hi placeholder:text-lo"
        />

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <label className="flex items-center gap-1.5 text-[12px] text-mid">
            <input
              type="checkbox"
              data-testid="call-note-billable"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
            />
            Billable
          </label>
          <Button
            variant="primary"
            data-testid="call-note-save"
            disabled={!note.trim() || saving}
            onClick={() => void handleLog()}
            className="text-[12px]"
          >
            <Icon name="circle-check" size={13} />
            {saving ? 'Logging…' : 'Log time to Ticket'}
          </Button>
          {/* ONLY WITHOUT A TICKET, and that is the whole honesty of the label.
              What reaches Halo on a close is decided by ticket-link state, so
              "stays here" is true precisely when there is nothing linked. With
              a ticket attached, closing correctly writes the remainder across —
              so offering this there would be a promise the system would break. */}
          {!hasTicket ? (
            <Button
              variant="secondary"
              data-testid="call-note-save-close"
              disabled={!note.trim() || saving}
              onClick={() => void handleCloseHere()}
              className="text-[12px]"
            >
              <Icon name="circle-check" size={13} />
              {saving ? 'Saving…' : 'Log & Close - No Ticket'}
            </Button>
          ) : null}
          {!hasTicket && matched ? (
            <Button
              variant="secondary"
              data-testid="call-note-quick-fix"
              disabled={!note.trim() || saving}
              onClick={() => void handleQuickFix()}
              className="text-[12px]"
            >
              <Icon name="circle-check" size={13} />
              {saving ? 'Saving…' : 'Quick Fix — Ticket & Close'}
            </Button>
          ) : null}
          <span className="text-[11px] text-lo">
            {hasTicket
              ? 'Writes a time entry on the linked ticket.'
              : /* No longer a dead end: the note is kept, and opens the ticket
                   if one is created — or crosses over if one is attached. Say
                   so, so nobody holds off writing it until after the ticket.
                   Matched adds the Quick Fix clause — it is the one path here
                   that both bills the time AND closes the thread in one step,
                   so it deserves its own sentence rather than folding into
                   "create a ticket". Unmatched keeps today's exact wording:
                   there is nothing to Quick Fix without an identified caller. */
                `Kept on this call — nothing goes to Halo without a ticket. Create a ticket and it opens with this note; attach one and it goes across.${
                  matched
                    ? ' Or Quick Fix it — that creates a Quick Fix ticket, bills the time, and closes it, all in one.'
                    : ''
                } Or close it here for a call nobody needs a ticket for, and it stays findable in Tossker.`}
          </span>
        </div>

        {logError ? (
          <p role="alert" data-testid="call-note-error" className="mt-1 text-[12px] text-bad">
            {logError}
          </p>
        ) : null}
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-lo">
        Voice is read-only here — 3CX owns the audio. To speak to them again, ring back.
      </p>
    </div>
  )
}
